import { equals } from 'ramda';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { IFeaturesMap, IRBushItem } from '../../../../../../common/types/map';
import { IApplicationState } from '../../../../../../common/types/redux';
import { getTilesToUpdate } from '../../../../../../common/utils/tiles';
import { selectMapBounds } from '../../../../../selectors';
import { TMapBounds } from '../../../../../types/mapBounds';
import { useFeaturesContext } from '../context';

export const FeaturesPropertiesUpdater: React.FC = () => {
  const { rbush, pointsLayer } = useFeaturesContext();
  const { bounds, zoom } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const features = useSelector<IApplicationState, IFeaturesMap>(state =>
    state.map.features[state.map.mapBounds.precision] ? state.map.features[state.map.mapBounds.precision].features : {},
  );
  const prevFeaturesRef = React.useRef<IFeaturesMap>(features);
  const prevFeatures = prevFeaturesRef.current;

  React.useEffect(() => {
    const updatedFeatures = Object.values(features).filter(({ id, properties }) => {
      const prevFeature = prevFeatures[id];
      if (!prevFeature) {
        return false;
      }

      if (!equals(properties, prevFeature.properties)) {
        return true;
      }

      return false;
    });

    const rbushItemsToUpdate: IRBushItem[] = [];
    for (const feature of updatedFeatures) {
      const featuresInRbush = rbush.search({
        minX: feature.geometry.coordinates[0],
        maxX: feature.geometry.coordinates[0],
        minY: feature.geometry.coordinates[1],
        maxY: feature.geometry.coordinates[1],
      });

      const featureInRbush = featuresInRbush.find(({ feature: { id } }) => id === feature.id);
      if (featureInRbush) {
        featureInRbush.feature.properties = feature.properties;
        rbushItemsToUpdate.push(featureInRbush);
      }
    }

    if (pointsLayer && rbushItemsToUpdate.length > 0) {
      const tilesToUpdate = getTilesToUpdate({ rbushItems: rbushItemsToUpdate, bounds, zoom, includeShadow: true });
      pointsLayer.layer.update(tilesToUpdate);
    }

    prevFeaturesRef.current = features;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features]);

  return null;
};
